import { useMemo, useState } from 'react';
import { Badge, Box, Button, Flex, ScrollArea, Spinner, Text, TextField } from '@radix-ui/themes';
import { AppWindow as AppWindowIcon, ChevronDown, ChevronRight, Play, Search, Square, X } from 'lucide-react';
import type { AppList, AppWindow } from '@mac-remote/protocol';

/** Same subsequence match the project list uses — "gc" should find "Google Chrome". */
function fuzzy(needle: string, haystack: string): boolean {
  if (!needle) return true;
  const n = needle.toLowerCase();
  const h = haystack.toLowerCase();
  if (h.includes(n)) return true;
  let i = 0;
  for (const ch of h) if (ch === n[i]) i++;
  return i === n.length;
}

export function Apps({
  apps,
  windows,
  pending,
  expanded,
  onExpand,
  onOpen,
  onQuit,
  onCloseWindow,
}: {
  apps: AppList | null;
  /** Windows per bundle id, filled in only for apps that have been expanded — the
   *  Mac spends ~250ms per app answering, and ~3s if asked about all of them. */
  windows: Record<string, AppWindow[] | undefined>;
  /** Bundle id or path currently being acted on — one at a time, so a slow graceful
   *  quit cannot be mistaken for a dead button. */
  pending: string | null;
  expanded: string | null;
  onExpand: (bundleId: string | null) => void;
  onOpen: (path: string, name: string) => void;
  onQuit: (bundleId: string, name: string) => void;
  onCloseWindow: (bundleId: string, index: number, title: string) => void;
}) {
  const [q, setQ] = useState('');

  // Matched by name: the installed list is a directory scan and carries no bundle id,
  // and reading Info.plist for several hundred bundles to get one would cost far more
  // than this label is worth.
  const runningNames = useMemo(
    () => new Set((apps?.running ?? []).map((a) => a.name)),
    [apps],
  );

  // Only worth searching what is not already open — the running list is right above.
  const matches = useMemo(() => {
    if (!apps || !q.trim()) return [];
    return apps.installed.filter((a) => fuzzy(q, a.name)).slice(0, 40);
  }, [apps, q]);

  if (!apps) {
    return (
      <Flex align="center" justify="center" gap="2" style={{ height: '100%', padding: 24 }}>
        <Spinner size="1" />
        <Text size="2" style={{ color: 'var(--app-hint)' }}>
          Спрашиваю мак…
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" style={{ height: '100%', minHeight: 0 }}>
      <Box p="2">
        <TextField.Root
          placeholder="Найти приложение, чтобы запустить"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          size="3"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          style={{ fontSize: 16 }}
        >
          <TextField.Slot>
            <Search size={16} />
          </TextField.Slot>
        </TextField.Root>
      </Box>

      <ScrollArea style={{ flex: 1, minHeight: 0 }}>
        <Flex direction="column" gap="3" p="2" pt="0">
          {q.trim() && (
            <Box>
              <Text size="1" weight="bold" style={{ color: 'var(--app-hint)', letterSpacing: 0.4 }}>
                УСТАНОВЛЕННЫЕ
              </Text>
              <Flex direction="column" gap="1" mt="2">
                {matches.length === 0 && (
                  <Text size="2" style={{ color: 'var(--app-hint)', padding: '8px 2px' }}>
                    Ничего не нашлось
                  </Text>
                )}
                {matches.map((a) => {
                  const isRunning = runningNames.has(a.name);
                  return (
                    <Flex key={a.path} align="center" gap="2" className="project-row">
                      <AppWindowIcon size={16} style={{ color: 'var(--app-hint)', flex: '0 0 auto' }} />
                      <Text
                        size="2"
                        style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {a.name}
                      </Text>
                      {isRunning ? (
                        <Badge size="1" color="jade" variant="soft">
                          запущено
                        </Badge>
                      ) : (
                        <Button
                          size="1"
                          variant="soft"
                          disabled={pending !== null}
                          loading={pending === a.path}
                          onClick={() => onOpen(a.path, a.name)}
                        >
                          <Play size={12} />
                          Открыть
                        </Button>
                      )}
                    </Flex>
                  );
                })}
              </Flex>
            </Box>
          )}

          <Box>
            <Text size="1" weight="bold" style={{ color: 'var(--app-hint)', letterSpacing: 0.4 }}>
              ЗАПУЩЕНЫ СЕЙЧАС
            </Text>
            <Flex direction="column" gap="1" mt="2">
              {apps.running.length === 0 && (
                <Text size="2" style={{ color: 'var(--app-hint)', padding: '8px 2px' }}>
                  Ни одного окна не открыто
                </Text>
              )}
              {apps.running.map((a) => {
                const open = expanded === a.bundleId;
                const wins = windows[a.bundleId];
                return (
                  <Box key={a.bundleId}>
                    <Flex align="center" gap="2" className="project-row">
                      {/* Tapping the name expands rather than acts: the destructive
                          button stays a separate, deliberate target. */}
                      <Flex
                        align="center"
                        gap="2"
                        style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                        onClick={() => onExpand(open ? null : a.bundleId)}
                      >
                        {open ? (
                          <ChevronDown size={16} style={{ color: 'var(--app-hint)', flex: '0 0 auto' }} />
                        ) : (
                          <ChevronRight size={16} style={{ color: 'var(--app-hint)', flex: '0 0 auto' }} />
                        )}
                        <Text
                          size="2"
                          style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {a.name}
                        </Text>
                        {wins && (
                          <Badge size="1" color="gray" variant="soft">
                            {wins.length === 0 ? 'без окон' : `${wins.length} окон`}
                          </Badge>
                        )}
                      </Flex>
                      <Button
                        size="1"
                        variant="soft"
                        color="red"
                        disabled={pending !== null}
                        loading={pending === a.bundleId}
                        onClick={() => onQuit(a.bundleId, a.name)}
                      >
                        <Square size={11} />
                        Всё
                      </Button>
                    </Flex>

                    {open && (
                      <Flex direction="column" gap="1" mt="1" style={{ paddingLeft: 22 }}>
                        {!wins && (
                          <Flex align="center" gap="2" style={{ padding: '6px 2px' }}>
                            <Spinner size="1" />
                            <Text size="1" style={{ color: 'var(--app-hint)' }}>
                              Смотрю окна…
                            </Text>
                          </Flex>
                        )}
                        {wins?.length === 0 && (
                          <Text size="1" style={{ color: 'var(--app-hint)', padding: '6px 2px' }}>
                            У приложения нет открытых окон — закрыть можно только целиком
                          </Text>
                        )}
                        {wins?.map((w) => (
                          <Flex
                            key={`${w.index}:${w.title}`}
                            align="center"
                            gap="2"
                            style={{
                              padding: '8px 10px',
                              borderRadius: 8,
                              background: 'var(--app-secondary-bg)',
                            }}
                          >
                            <Text
                              size="1"
                              style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {w.title || '(без заголовка)'}
                            </Text>
                            <Button
                              size="1"
                              variant="ghost"
                              color="red"
                              disabled={pending !== null}
                              loading={pending === `${a.bundleId}#${w.index}`}
                              onClick={() => onCloseWindow(a.bundleId, w.index, w.title)}
                            >
                              <X size={13} />
                            </Button>
                          </Flex>
                        ))}
                      </Flex>
                    )}
                  </Box>
                );
              })}
            </Flex>
          </Box>

          <Text size="1" style={{ color: 'var(--app-hint)', padding: '0 2px' }}>
            Тап по названию — список окон, их можно закрывать по одному. «Всё» закрывает
            приложение целиком: сначала по-хорошему, а если оно держит несохранённое —
            предложу принудительно, и только тогда работа потеряется.
          </Text>
        </Flex>
      </ScrollArea>
    </Flex>
  );
}
